import { createContext, useContext, useEffect, useState } from "react";
import { useFirestore } from "../firebase";

const format = (docSnapshot) => ({
	_id: docSnapshot.id,
	...docSnapshot.data(),
});

export const Context = createContext(null);

export const useMe = () => {
	const firestore = useFirestore();
	const user = useContext(Context);

	const recordMood = async (key) => {
		const mood = {
			createdAt: new Date(),
			key,
		};

		await firestore()
			.collection("users")
			.doc(user.uid)
			.collection("moods")
			.add(mood);
	};

	const redoMood = async () => {
		await recordMood("REDO");
	};

	return {
		user,
		api: {
			recordMood,
			redoMood,
		},
	};
};

export const useExternalState = () => {
	const firestore = useFirestore();
	const user = useContext(Context);
	const [quizStates, setQuizStates] = useState(null);
	const [logicStates, setLogicStates] = useState(null);
	const [contentStates, setContentStates] = useState(null);
	const [bookmarkStates, setBookmarkStates] = useState(null);
	const [userState, setUserState] = useState(null);

	useEffect(() => {
		if (user) {
			const quizUnsubscribeFn = firestore()
				.collection("users")
				.doc(user.uid)
				.collection("quizStates")
				.onSnapshot((querySnapshot) => {
					if (querySnapshot) {
						const _quizStates = querySnapshot.docs.map(format);

						setQuizStates(_quizStates);
					}
				});

			const logicUnsubscribeFn = firestore()
				.collection("users")
				.doc(user.uid)
				.collection("logicStates")
				.onSnapshot((querySnapshot) => {
					if (querySnapshot) {
						const _logicStates = querySnapshot.docs.map(format);

						setLogicStates(_logicStates);
					}
				});

			const contentUnsubscribeFn = firestore()
				.collection("users")
				.doc(user.uid)
				.collection("contentStates")
				.onSnapshot((querySnapshot) => {
					if (querySnapshot) {
						const _contentStates = querySnapshot.docs.map(format);
						setContentStates(_contentStates);
					}
				});

			const bookmarkUnsubscribeFn = firestore()
				.collection("users")
				.doc(user.uid)
				.collection("bookmarkStates")
				.onSnapshot((querySnapshot) => {
					if (querySnapshot) {
						const _bookmarkStates = querySnapshot.docs.map(format);
						setBookmarkStates(_bookmarkStates);
					}
				});

			const userUnsubscribeFn = firestore()
				.collection("users")
				.doc(user.uid)
				.onSnapshot((docSnapshot) => {
					if (docSnapshot) {
						setUserState(docSnapshot.data());
					}
				});

			return () => {
				quizUnsubscribeFn();
				logicUnsubscribeFn();
				contentUnsubscribeFn();
				userUnsubscribeFn();
				bookmarkUnsubscribeFn();
			};
		}

		setQuizStates(null);
		setLogicStates(null);
		setContentStates(null);
		setUserState(null);
		setBookmarkStates(null);
		return () => {}; // noop unsubscribe func
	}, [user]);

	const addQuiz = async (quizState, logicState) => {
		const { quizId } = quizState;

		// clear out old quiz history
		await removeQuizById(quizId);

		// save new states
		const quizStateDoc = firestore()
			.collection("users")
			.doc(user.uid)
			.collection("quizStates")
			.doc();
		const logicStateDoc = firestore()
			.collection("users")
			.doc(user.uid)
			.collection("logicStates")
			.doc();

		await quizStateDoc.set({
			...quizState,
			dateCompleted: new Date().toISOString(),
		});
		await logicStateDoc.set({
			quizId,
			...logicState.internal,
		});
	};

	/**
	 * Set an arbitrary global flag for use in content tailoring
	 * @param flag <String>
	 */
	const setGlobalFlag = async (flag: string) => {
		const logicStateDoc = firestore()
			.collection("users")
			.doc(user.uid)
			.collection("logicStates")
			.doc();

		await logicStateDoc.set({
			global: {
				[flag]: true,
			},
			local: {},
			quizId: null,
			stream: [],
		});
	};

	const removeQuizById = async (quizId) => {
		const firestore = useFirestore();
		const quizStates = await firestore()
			.collection("users")
			.doc(user.uid)
			.collection("quizStates")
			.where("quizId", "==", quizId)
			.get();

		const logicStates = await firestore()
			.collection("users")
			.doc(user.uid)
			.collection("logicStates")
			.where("quizId", "==", quizId)
			.get();

		await Promise.all(
			quizStates.docs.map(async (s) => {
				await s.ref.delete();
			})
		);

		await Promise.all(
			logicStates.docs.map(async (s) => {
				await s.ref.delete();
			})
		);
	};

	const markContentAsRead = async (contentId: string) => {
		const contentState = {
			contentId,
			readAt: new Date(),
		};
		await firestore()
			.collection("users")
			.doc(user.uid)
			.collection("contentStates")
			.doc(contentId)
			.set(contentState);
	};

	const bookmarkContent = async (contentId: string) => {
		const bookmark = {
			contentId,
			readAt: new Date(),
		};
		try {
			await firestore()
				.collection("users")
				.doc(user.uid)
				.collection("bookmarkStates")
				.doc(contentId)
				.set(bookmark);
		} catch (err) {
			console.log(err);
		}
	};

	const unBookmarkContent = async (contentId: string) => {
		await firestore()
			.collection("users")
			.doc(user.uid)
			.collection("bookmarkStates")
			.doc(contentId)
			.delete();
	};

	return {
		quizStates,
		logicStates,
		contentStates,
		bookmarkStates,
		userState,
		api: {
			addQuiz,
			removeQuizById,
			markContentAsRead,
			setGlobalFlag,
			bookmarkContent,
			unBookmarkContent,
		},
	};
};
